import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { SIcon, type SIconName } from '../SIcon';
import { TAG_SIZE_CONFIG, type STagShape, type STagSize, type STagColor } from './tag.config';

export interface STagProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** 모양 */
  shape?: STagShape;
  /** 크기 */
  size?: STagSize;
  /** 색상 */
  color?: STagColor;
  /** 레이블 텍스트 */
  label?: string;
  /** 아이콘명 */
  icon?: SIconName;
  /** 아이콘을 레이블 왼쪽에 배치 */
  iconLeft?: boolean;
  /** 태그 너비 (숫자면 px, 문자열이면 그대로 적용). 미지정 시 콘텐츠 크기 */
  width?: string | number;
}

/**
 * STag — sd-tag 포팅.
 * Stencil의 `name`(예: square_sm_grey) 프리셋을 shape/size/color 세 prop으로 분리.
 * 색상은 컴포넌트 토큰 `--cmp-tag-<color>-{bg,content}` 참조.
 */
export const STag = forwardRef<HTMLSpanElement, STagProps>(function STag(
  {
    shape = 'square',
    size = 'sm',
    color = 'grey',
    label = '',
    icon,
    iconLeft = true,
    width,
    className,
    style,
    ...rest
  },
  ref,
) {
  const s = TAG_SIZE_CONFIG[size];
  const contentColor = `var(--cmp-tag-${color}-content)`;
  const iconNode = icon ? <SIcon name={icon} size={s.iconSize} color={contentColor} /> : null;
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <span
      ref={ref}
      aria-label={label || 'tag'}
      className={cn(
        'box-border inline-flex w-fit select-none items-center justify-center whitespace-nowrap',
        className,
      )}
      style={{
        minHeight: s.height,
        padding: `0 ${s.paddingX}px`,
        gap: s.gap,
        borderRadius: shape === 'pill' ? 999 : s.radius,
        fontSize: s.fontSize,
        fontWeight: s.fontWeight,
        lineHeight: `${s.lineHeight}px`,
        background: `var(--cmp-tag-${color}-bg)`,
        color: contentColor,
        ...(resolvedWidth != null ? { width: resolvedWidth } : {}),
        ...style,
      }}
      {...rest}
    >
      {icon && iconLeft && iconNode}
      {label && <span className="inline-flex min-w-0 items-center justify-center">{label}</span>}
      {icon && !iconLeft && iconNode}
    </span>
  );
});
